import {
  ImageScaleView,
  ImageScaleViewOpts,
  View,
} from '@play-co/timestep-core/ui';
import { DOWNSCALE_FACTOR, downscale } from 'src/conf/scale';
import { Match2RewardsPanel } from 'src/game/minigame/match2-timestep/components/Match2RewardsPanel';
import { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';

interface ChestRewardsTooltipOpts extends ImageScaleViewOpts {
  superview: View;
  parentSuperview: View;
  above?: boolean; // defines whether tip is above or below rewards panel
  itemSize?: number;
  itemPadding?: number;
  itemLabelSize?: number;
  itemLabelOffsetX?: number;
  itemLabelOffsetY?: number;
  itemOffsetX?: number;
  itemOffsetY?: number;
  tracking: { feature: string; $subFeature: string };
  noBg?: boolean;
}

export class ChestRewardsTooltip extends ImageScaleView {
  private readonly tip: ImageScaleView;
  private readonly rewards: Match2RewardsPanel;
  private readonly overlay: View;
  private hideTimeout?: ReturnType<typeof setTimeout>;

  constructor(opts: ChestRewardsTooltipOpts) {
    super({
      image:
        'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/reward_frame.png',
      width: downscale(500),
      height: downscale(160),
      imgScale: DOWNSCALE_FACTOR,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 60, right: 60 },
        vertical: { top: 60, bottom: 60 },
      },
      ...opts,
    });

    // Create a full-screen overlay to capture clicks outside
    this.overlay = new View({
      superview: opts.parentSuperview,
      infinite: true,
      canHandleEvents: true,
      visible: false,
      zIndex: (opts.zIndex || 0) - 1,
    });

    // Handle clicks on the overlay to hide the tooltip
    this.overlay.on('InputSelect', () => {
      this.hideRewards();
    });

    this.tip = new ImageScaleView({
      superview: this,
      image:
        'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/reward_frame_tip.png',
      width: downscale(70),
      height: downscale(39),
      x: this.style.width * 0.5,
      r: (opts.above ?? false) ? 0 : Math.PI,
      y: (opts.above ?? false) ? 12 : this.style.height - 4,
      centerOnOrigin: true,
    });

    this.rewards = new Match2RewardsPanel({
      superview: this,
      centerOnOrigin: false,
      width: this.style.width - 30,
      height: this.style.height - 40,
      x: 15,
      y: 22,
      itemSize: opts.itemSize ?? 60,
      itemPadding: opts.itemPadding ?? 5,
      itemLabelSize: opts.itemLabelSize ?? 25,
      itemOffsetY: opts.itemOffsetY ?? 15,
      itemLabelOffsetY: opts.itemLabelOffsetY ?? -5,
      noBg: opts.noBg ?? true,
    });
  }

  public updateOpts(opts: ImageScaleViewOpts) {
    const result = super.updateOpts(opts);
    this.tip.updateOpts({
      x: this.style.width * 0.5,
    });
    return result;
  }

  public setRewards(rewards: Match2Reward[]) {
    this.rewards.setRewards(rewards);
  }

  public showRewards() {
    // Show overlay
    this.show();
    this.overlay.style.visible = true;

    // Clear any existing hide timeout
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }

    // Auto-hide after 3 seconds
    this.hideTimeout = setTimeout(() => {
      this.hideRewards();
    }, 3000);
  }

  public hideRewards() {
    // Hide overlay
    this.hide();
    this.overlay.style.visible = false;

    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
  }
}
